import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useStripeFinancialFeaturesEnabled } from '@/hooks/useStripeFinancialFeaturesEnabled';
import { ChurnRiskAnalysis, MemberOverview } from '@/models/AudienceByWorkspace';
import { ChurnRiskData, RiskFactor } from '@/models/ChurnRisk';
import { CustomCard } from '@/modules/Dashboard/DashboardPage/Components/MetricComponents';

import NoDataSection from '../NoDataSection';

import ChurnRiskIndicator from './ChurnRiskIndicator';
import RiskFactorsList from './RiskFactorsList';

/** When the API sends no structured factors, show a few plain lines from score + activity + status. */
function simpleRiskFactors(
  riskScore: number,
  churnData: ChurnRiskData | undefined,
  memberData: MemberOverview | undefined,
): RiskFactor[] {
  const detectedAt = new Date().toISOString();
  const out: RiskFactor[] = [];

  if (riskScore >= 60) {
    out.push({
      type: 'score',
      severity: 'high',
      description: 'Churn risk score is high',
      detectedAt,
    });
  } else if (riskScore >= 30) {
    out.push({
      type: 'score',
      severity: 'medium',
      description: 'Churn risk score is moderate',
      detectedAt,
    });
  } else {
    out.push({
      type: 'score',
      severity: 'low',
      description: 'Churn risk score is low',
      detectedAt,
    });
  }

  const m = churnData?.activityMetrics;
  if (m) {
    if (m.daysSinceLastActivity < 0 || m.totalActivities === 0) {
      out.push({
        type: 'activity',
        severity: 'medium',
        description: 'No product events in the last 30 days',
        detectedAt,
      });
    } else if (m.daysSinceLastActivity >= 14) {
      out.push({
        type: 'activity',
        severity: 'medium',
        description: `Last activity about ${m.daysSinceLastActivity} days ago`,
        detectedAt,
      });
    }
  }

  const status = (memberData?.status ?? churnData?.subscriptionStatus ?? '').toLowerCase();
  if (status === 'expired' || status === 'cancelled' || status === 'past_due') {
    out.push({
      type: 'subscription',
      severity: status === 'past_due' ? 'high' : 'medium',
      description: `Subscription is ${status.replace(/_/g, ' ')}`,
      detectedAt,
    });
  }

  return out.slice(0, 3);
}

interface ChurnRiskSectionProps {
  memberData?: MemberOverview;
  churnData: ChurnRiskData | undefined;
  churnRiskAnalysis?: ChurnRiskAnalysis;
  changeActiveStep: (step: string) => void;
}

const ChurnRiskSection: React.FC<ChurnRiskSectionProps> = ({
  memberData,
  churnData,
  churnRiskAnalysis,
  changeActiveStep,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { t } = useTranslation();
  const showStripeFinancial = useStripeFinancialFeaturesEnabled();

  const riskScore = churnRiskAnalysis?.riskScore ?? churnData?.riskScore ?? 0;

  const riskFactors = useMemo((): RiskFactor[] => {
    const fromAnalysis =
      churnRiskAnalysis?.riskFactors?.map(
        (f: { description: string; severity: 'low' | 'medium' | 'high'; impact: number }) => ({
          type: 'custom',
          severity: f.severity as 'low' | 'medium' | 'high',
          description: f.description,
          detectedAt: new Date().toISOString(),
        }),
      ) ?? [];

    if (fromAnalysis.length > 0) {
      return fromAnalysis;
    }

    const fromChurn = (churnData?.riskFactors ?? []).map((f: string | RiskFactor) =>
      typeof f === 'string'
        ? {
            type: 'custom',
            severity: 'medium' as const,
            description: f,
            detectedAt: new Date().toISOString(),
          }
        : f,
    );

    if (fromChurn.length > 0) {
      return fromChurn;
    }

    return simpleRiskFactors(riskScore, churnData, memberData);
  }, [churnRiskAnalysis?.riskFactors, churnData, memberData, riskScore]);

  const lifetimeValue = churnData?.lifetimeValue ?? memberData?.stripeTotalLifetimeValue ?? 0;

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - Type instantiation depth issue with complex nested types
    <CustomCard
      title={t('MemberProfilePage.churnRiskLabel', 'Churn Risk Analysis')}
      className="flex h-full min-h-0 w-full flex-1 flex-col"
    >
      {!churnData && !churnRiskAnalysis ? (
        <div className="flex min-h-[10rem] flex-1 flex-col">
          <NoDataSection />
        </div>
      ) : (
        <div className="mt-4 flex min-h-0 flex-1 flex-col gap-4 sm:flex-row">
          <ChurnRiskIndicator score={riskScore} />

          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
            <div>
              <div className="text-custom-darkSilver mb-2 text-xs font-semibold">
                {t('MemberProfilePage.riskSummaryLabel', 'Risk summary')}
              </div>
              <RiskFactorsList factors={riskFactors.slice(0, 3)} />
            </div>

            {showStripeFinancial && (
              <div className="flex items-center justify-between text-xs text-custom-darkSilver">
                <span>Lifetime Value</span>
                <span className="font-semibold text-custom-black">${lifetimeValue.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </CustomCard>
  );
};

export default ChurnRiskSection;
